import { useQuery } from '@tanstack/react-query'

import { businessDayQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import { useAuth } from '@/auth/use-auth'

/**
 * The branch's business day, for the screens that post into it.
 *
 * The date a posting lands on is the ledger's, not the browser's: a branch that has not closed
 * yesterday is still working in yesterday. So every value date on these screens is seeded from
 * the business date and never from `new Date()`.
 *
 * `businessDate` is null whenever no day is open, which is the ordinary state at seven in the
 * morning and is exactly the case a posting screen exists to catch. When it is null there is no
 * date to seed with and nothing will post, and the screens say so rather than quietly
 * substituting today — which would produce a value date the ledger refuses after the member has
 * already handed over the cash.
 */
export function useBranchId(): string | null {
  const { user } = useAuth()
  return session.getBranchId() ?? user?.homeBranchId ?? null
}

export function useBusinessDay() {
  const branchId = useBranchId()
  return useQuery(businessDayQueryOptions(branchId))
}

/**
 * The seed for a value date, or null when there is none.
 *
 * Never falls back to the browser's clock. A screen that cannot learn the business date lets the
 * operator key one, and says why it could not fill it in.
 */
export function useBusinessDate(): {
  date: string | null
  isPending: boolean
  acceptsPostings: boolean
} {
  const query = useBusinessDay()

  return {
    date: query.data?.businessDate ?? null,
    isPending: query.isPending,
    acceptsPostings: query.data?.acceptsPostings ?? false,
  }
}
